import { createGqlFetcher } from 'app/lib/gqlFetcher'
import { observer } from 'mobx-react-lite'
import { ArrowPathIcon } from '@heroicons/react/20/solid'
import { withZod } from '@remix-validated-form/with-zod'
import classNames from 'classnames'
import { isEmpty, omitBy } from 'lodash'
import { useEffect, useState } from 'react'
import { z } from 'zod'
import { AddNewIntegrationModal } from '../AddNewIntegrationModal'
import { REMOVE_LOCATION_INTEGRATION } from '../ops-graphql'
import Button from 'app/components/Button'
import { useConfirmation } from 'app/components/ConfirmationContext'
import FormCombo from 'app/components/form/FormCombo'
import FormComboAsync from 'app/components/form/FormComboAsync'
import FormComboMultiSelect from 'app/components/form/FormComboMultiSelect'
import FormSelect from 'app/components/form/FormComboSelect'
import { SmartForm } from 'app/components/form/SmartForm'
import Spinner from 'app/components/Spinner'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { CONVERSION_TYPES } from 'app/types/entities'
import type { LocationJson, LocationType } from 'app/types/entities'
import { closeModal, showModal } from 'app/lib/modal'
import { gqlOpsMutation } from 'app/lib/opsGraphqlClient'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'
import { OptionType } from 'types/OptionType'

const CONVERSION_TYPE_LABELS = {
  QUALIFIED_LEAD_GENERATED: `Qualified Lead Generated`,
  NEW_PATIENT_APPOINTMENT_SCHEDULED: `New Patient Appointment Scheduled`,
  NEW_PATIENT_GENERATED: `New Patient Generated`,
  ADDITIONAL_TREATMENT_REVENUE: `Additional Treatment Revenue (90 Day Window)`,
} as const

const createSchema = z.object({
  locationId: z.string().min(1, { message: `A Location is required` }),
  account: z.string().min(1, { message: `An Account is required` }),
  // The form serialises campaigns as { id, name, value } hidden inputs only
  // (FormComboMultiSelect never emits the option's meta), so the schema accepts
  // that shape just to pass validation — it does not drive the payload. onSubmit
  // builds the saved campaigns from selectedCampaignOptions, which keeps each
  // option's real meta.type (campaign vs adGroup) so the backend can keep only
  // campaigns, matching the sibling Facebook Ads form (TRK-6085).
  campaigns: z
    .array(
      z.object({
        id: z.string(),
        name: z.string().optional(),
        value: z.string().optional(),
      }),
    )
    .optional(),
  passback: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        value: z.string(),
      }),
    )
    .transform((val) => {
      // Handle "All" and "None" special cases
      if (val.length === 1) {
        if (val[0].id === `All`) {
          return CONVERSION_TYPES
        }
        if (val[0].id === `None`) {
          return []
        }
      }
      // Return individual conversion types
      return val.map((v) => v.value)
    })
    .optional(),
})

export const createGoogleAdsValidator = withZod(createSchema)

// observer: reads a feature toggle (and possibly authStore.user) in render; useFeatureToggle resolves async and its contract requires an observer wrapper (TRK-6020).
export const GoogleAdsFormModal = observer(function GoogleAdsFormModal({
  onAdd,
  currentValues,
}: {
  onAdd: () => void
  currentValues?: {
    locationId: string
    locationName: string
    googleAdsV3?: LocationType[`googleAdsV3`]
  }
}) {
  const [selectedLocation, setSelectedLocation] = useState<OptionType | undefined>()
  const [accountsResponse, setAccountsResponse] = useState<{
    data?: {
      account: {
        id: string | undefined
        name: string | null | undefined
        isUsed: boolean
        isDisabled: boolean
      }
    }[]
    error?: any
  }>()
  const [campaignsResponse, setCampaignsResponse] = useState<{
    data?: {
      campaigns: {
        id: string | null | undefined
        name?: string | null
        isUsed: boolean
        isDisabled: boolean
        adGroups?: {
          id: string | null | undefined
          name?: string | null
          isUsed: boolean
        }[]
      }[]
    }
    error?: any
  }>()
  const [accountOptions, setAccountOptions] = useState<OptionType[]>([])
  const [accountsLoading, setAccountsLoading] = useState(false)
  const [accountsError, setAccountsError] = useState(false)
  const [campaignOptions, setCampaignOptions] = useState<OptionType[]>([])
  const [selectedAccountOption, setSelectedAccountOption] = useState<OptionType | undefined>(undefined)
  const [selectedCampaignOptions, setSelectedCampaignOptions] = useState<OptionType[]>([])
  const [confirm] = useConfirmation()
  const [isSaving, setIsSaving] = useState(false)
  const [selectedPassbackOptions, setSelectedPassbackOptions] = useState<OptionType[]>([
    { name: `None`, id: `None`, value: `None` },
  ])
  const passbackEnabled = useFeatureToggle(`TRK-3672`)

  const fetchAccountOptions = (refresh?: boolean) => {
    const composedSearchParams = new URLSearchParams(
      omitBy({ refresh: refresh ? `true` : ``, locationId: currentValues?.locationId ?? `` }, isEmpty),
    )
    const url = `/api/integrations/google/accounts?${composedSearchParams.toString()}`

    setAccountsResponse(undefined)
    setAccountOptions([])
    setAccountsError(false)
    setAccountsLoading(true)
    bg(async () => {
      try {
        const res = await window.fetch(url, { credentials: `include` })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const response = await res.json()
        setAccountsResponse(response)
      } catch (err) {
        logError(`Failed to load Google Ads account options:`, err)
        setAccountsError(true)
        // Surface a toast only for an explicit user-triggered refresh; the inline
        // "Failed to load accounts" message covers the automatic mount-time load.
        if (refresh) {
          notifyError(`There was a problem loading options. You may need to reload the page.`)
        }
      } finally {
        setAccountsLoading(false)
      }
    })
  }

  const fetchCampaignOptions = (refresh?: boolean) => {
    const composedSearchParams = new URLSearchParams(
      omitBy({ refresh: refresh ? `true` : ``, locationId: currentValues?.locationId ?? `` }, isEmpty),
    )
    const url = `/api/integrations/google/accounts/${selectedAccountOption?.id}?${composedSearchParams.toString()}`

    setCampaignsResponse(undefined)
    setCampaignOptions([])
    if (url) {
      bg(async () => {
        try {
          let response = await window.fetch(url, { credentials: `include` }).then((r) => r.json())
          setCampaignsResponse(response)
        } catch (err) {
          logError(`Failed to load Google Ads campaign options:`, err)
          notifyError(`There was a problem loading options. You may need to reload the page.`)
        }
      })
    }
  }

  const onRefreshClick = () => {
    fetchCampaignOptions(true)
  }

  const onCreateCompleted = (data: any) => {
    setIsSaving(false)
    if (data.success) {
      notifySuccess(`Google Ads integration created, reloading data...`)
      onAdd()
      closeModal()
    } else {
      notifyError(`Failed to create integration: ${data.error}`)
    }
  }

  const onSubmit = async (data: any) => {
    setIsSaving(true)
    // Build the campaigns payload from the selected options, not the form data.
    // The form only serialises { id, name, value }, but the backend keeps only
    // entries whose type is `campaign`, so the real meta.type must be preserved
    // (an ad group selected in the combo must not be saved as a campaign id).
    // The deselect-all sentinel (id `0`) is dropped; an empty list means all
    // campaigns.
    const campaigns = selectedCampaignOptions
      .filter((opt) => opt.id !== `0`)
      .map((opt) => ({
        id: opt.id,
        type: opt.meta?.type ?? `campaign`,
        parentId: opt.meta?.parentId ?? selectedAccountOption?.id,
        grandparentId: opt.meta?.grandparentId,
      }))
    try {
      const response = await window.fetch(`/api/integrations/google`, {
        method: currentValues ? `PUT` : `POST`,
        credentials: `include`,
        headers: { 'Content-Type': `application/json` },
        body: JSON.stringify({
          locationId: data.locationId,
          account: data.account,
          campaigns,
          passback: data.passback,
        }),
      })
      const result = await response.json()
      onCreateCompleted(result)
    } catch (err) {
      logError(`Failed to save Google Ads integration:`, err)
      notifyError(`There was a problem saving the integration.`)
      setIsSaving(false)
    }
  }

  const goBackToSelectIntegrationModal = () => {
    closeModal()
    showModal(
      <AddNewIntegrationModal onIntegrationAdded={onAdd} />,
      {
        title: `Add New Integration`,
        isFullScreen: false,
        overflowHidden: false,
        removePadding: true,
        useRadix: true,
      },
      false,
    )
  }

  // Fetch options on mount
  useEffect(() => {
    fetchAccountOptions()
  }, [])

  // Fetch campaign options when account option is selected
  useEffect(() => {
    if (selectedAccountOption) {
      fetchCampaignOptions()
    }
  }, [selectedAccountOption])

  useEffect(() => {
    // accounts have loaded
    if (accountsResponse && accountsResponse.data) {
      const responseOptions = accountsResponse.data.map((hit: any) => ({
        id: hit.account.id || hit.account.name,
        name: hit.account.name,
        value: hit.account.id,
        meta: {
          isUsed: hit.account.isUsed,
          isDisabled: hit.account.isDisabled,
          tooltipForDisabledOption: `This account is connected to another location`,
          type: `account`,
        },
      }))
      setAccountOptions(responseOptions)

      // Load existing googleAdsV3 configuration
      if (currentValues?.googleAdsV3?.accountId) {
        const account = responseOptions.find((option) => option.id === currentValues.googleAdsV3!.accountId)
        if (account) {
          setSelectedAccountOption(account as OptionType)
        }
      }
    }
  }, [accountsResponse])

  useEffect(() => {
    // campaigns have loaded
    if (campaignsResponse && campaignsResponse.data) {
      const responseOptions = campaignsResponse.data.campaigns.map((campaign: any) => {
        // Format SystemGenerated campaign names to be more user-friendly
        let displayName = campaign.name
        if (campaign.name?.includes(`SystemGenerated`)) {
          // Extract the first part before "Campaign" and format it
          // e.g., "LocalServicesCampaign:SystemGenerated:xxx" -> "Local Services Campaign"
          const match = campaign.name.match(/^([A-Za-z]+)Campaign:SystemGenerated/)
          if (match) {
            // Convert from camelCase/PascalCase to Title Case
            // e.g., "LocalServices" -> "Local Services"
            displayName =
              match[1]
                .replace(/([A-Z])/g, ` $1`)
                .trim()
                .split(` `)
                .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(` `) + ` Campaign`
          }
        }

        return {
          id: campaign.id,
          name: displayName,
          value: campaign.id,
          meta: {
            isUsed: campaign.isUsed,
            isDisabled: campaign.isDisabled,
            tooltipForDisabledOption: `This campaign is connected to another location`,
            type: `campaign`,
            parentId: selectedAccountOption!.id,
            originalName: campaign.name, // Keep original name for reference
          },
          // Google Ads V3 stores campaign ids only — the backend intentionally
          // dropped ad-group support (see Location.googleAdsV3). Ad groups are
          // therefore not offered as selectable options; a selected ad group
          // would be ignored on save and could never re-select on reload.
        }
      })
      setCampaignOptions(responseOptions)

      // Handle campaign selection for googleAdsV3
      const selection: OptionType[] = []

      if (currentValues?.googleAdsV3?.campaignIds?.length) {
        currentValues.googleAdsV3.campaignIds.forEach((campaignId) => {
          const campaign = responseOptions.find(
            (option) => option.id === campaignId && option.meta.parentId === currentValues.googleAdsV3!.accountId,
          )
          if (campaign) {
            selection.push(campaign)
          }
        })
      }

      setSelectedCampaignOptions(selection)
    }
  }, [campaignsResponse])

  // Set selected conversion types for existing integrations
  useEffect(() => {
    const selectedConversionTypes: string[] = []

    if (currentValues?.googleAdsV3?.conversionActions) {
      Object.keys(currentValues.googleAdsV3.conversionActions).forEach((conversionType) => {
        if (!selectedConversionTypes.includes(conversionType)) {
          selectedConversionTypes.push(conversionType)
        }
      })
    }

    if (selectedConversionTypes.length > 0) {
      // If all 4 conversion types are selected, show "All"
      if (selectedConversionTypes.length === CONVERSION_TYPES.length) {
        setSelectedPassbackOptions([{ name: `All`, id: `All`, value: `All` }])
      } else {
        // Show individual selected conversion types
        const selectedOptions = selectedConversionTypes.map((conversionType) => ({
          id: conversionType,
          name: CONVERSION_TYPE_LABELS[conversionType as keyof typeof CONVERSION_TYPE_LABELS],
          value: conversionType,
        }))
        setSelectedPassbackOptions(selectedOptions)
      }
    } else {
      // No conversion types selected, show "None"
      setSelectedPassbackOptions([{ name: `None`, id: `None`, value: `None` }])
    }
  }, [currentValues])

  const onRemove = () => {
    return confirm({
      title: `Remove Google Ads Integration`,
      description: `Removing this integration will also remove all associated data. Do you want to continue?`,
      variant: `danger`,
    }).then(() =>
      gqlOpsMutation(REMOVE_LOCATION_INTEGRATION, {
        locationId: currentValues?.locationId!,
        integration: `google`,
      }).then((result) => {
        if (result.data?.removeLocationIntegration?.success) {
          notifySuccess(`Google Ads integration removed`)
          onAdd()
          closeModal()
        } else {
          notifyError(`Failed to remove integration: ${result.error || 'Unknown error'}`)
        }
      }),
    )
  }

  return (
    <>
      <SmartForm
        id="integration-form"
        className="!mt-0 w-[600px] grow-0 justify-start !gap-y-4 px-6 py-4"
        validator={createGoogleAdsValidator}
        reloadDocument={false}
        preventSubmit={true}
        onSubmit={onSubmit}
        hideButtons={true}
        hiddenInputs={currentValues ? [{ name: `locationId`, value: currentValues.locationId }] : undefined}
      >
        {currentValues ? (
          <FormSelect
            cols={5}
            name="locationId"
            label="Trackable Location"
            options={[{ label: currentValues.locationName, value: currentValues.locationId }]}
            value={currentValues.locationId}
            disabled
            labelClassName="text-body-sm text-strong"
          />
        ) : (
          <FormComboAsync
            cols={5}
            name="locationId"
            required={true}
            showSearchInDropdown={true}
            label="Trackable Location"
            value={selectedLocation}
            optionsMapper={(res) =>
              res?.hits?.map((hit: LocationJson) => ({
                id: hit.id || hit.name,
                name: hit.name,
                value: hit.id,
                meta: {
                  isUsed: !!hit.googleAdsV3,
                  tooltipForDisabledOption: `This location is already connected to Google Ads`,
                },
              }))
            }
            fetcher={createGqlFetcher('locations')}
            labelClassName="text-body-sm text-strong"
            onChange={(val) => setSelectedLocation(val)}
            newLineError={false}
            showErrorIcon
          />
        )}
        {accountOptions.length ? (
          <FormCombo
            cols={5}
            name="account"
            label="Account"
            value={selectedAccountOption}
            options={accountOptions}
            labelClassName="text-body-sm text-strong"
            showSearchInDropdown={true}
            onChange={(val) => {
              setSelectedAccountOption(val)
            }}
            // optionsContainerClassName="!w-[380px]"
            newLineError={false}
            showErrorIcon
          />
        ) : accountsLoading ? (
          <div className="col-span-5 flex items-center pt-5">
            <Spinner size={5} />
          </div>
        ) : accountsError ? (
          <div className="col-span-5 pt-5 text-sm text-red-600">Failed to load accounts</div>
        ) : (
          <div className="col-span-5 pt-5 text-sm text-subtle">No accounts available</div>
        )}
        {!selectedAccountOption ? null : campaignOptions.length ? (
          <>
            <FormComboMultiSelect
              cols={5}
              name="campaigns"
              label="Campaigns"
              values={selectedCampaignOptions}
              deselectAllOptions={[{ name: `All`, id: `0`, value: ``, meta: { type: `deselectAll` } }]}
              enableDeselectAllOption={true}
              options={campaignOptions}
              labelClassName="text-body-sm text-strong"
              onChange={(val: OptionType[]) => {
                setSelectedCampaignOptions(val)
              }}
              placeholder="All"
              // optionsContainerClassName="!w-[380px]"
              newLineError={false}
              checkboxSide="left"
              showErrorIcon
            />
            <div className="col-span-1 flex items-center justify-center pt-5">
              <ArrowPathIcon
                className={classNames(`ml-2 h-5 w-5 cursor-pointer`, !campaignsResponse && `animate-spin`)}
                onClick={() => onRefreshClick()}
              />
            </div>
          </>
        ) : (
          <div className="col-span-5 flex items-center pt-5">
            <Spinner size={5} />
          </div>
        )}
        {passbackEnabled && (
          <FormComboMultiSelect
            cols={5}
            name="passback"
            label="Conversion Types"
            values={selectedPassbackOptions}
            deselectAllOptions={[
              { name: `None`, id: `None`, value: `None` },
              { name: `All`, id: `All`, value: `All` },
            ]}
            enableDeselectAllOption={true}
            options={CONVERSION_TYPES.map((type) => ({
              id: type,
              name: CONVERSION_TYPE_LABELS[type],
              value: type,
            }))}
            labelClassName="text-body-sm text-strong"
            onChange={(val: OptionType[]) => {
              setSelectedPassbackOptions(val)
            }}
            newLineError={false}
            checkboxSide="left"
            showErrorIcon
          />
        )}
      </SmartForm>
      <div className="sticky bottom-0 w-full rounded-b-xl border-t border-default bg-surface-raised px-6 py-3">
        <div className="flex items-center justify-between">
          <div>
            {currentValues ? <Button label="Remove" mode="danger" size="md" onClick={() => onRemove()} /> : null}
          </div>
          <div className="flex gap-2">
            {!currentValues ? (
              <Button label="Back" mode="white" size="md" onClick={() => goBackToSelectIntegrationModal()} />
            ) : (
              <Button label="Cancel" mode="white" size="md" onClick={() => closeModal()} />
            )}

            <Button
              type="submit"
              form="integration-form"
              label="Save"
              mode="primary"
              size="md"
              disabled={(!selectedLocation && !currentValues?.locationId) || !selectedAccountOption}
              // onClick={() => setSaving(true)}
              loading={isSaving}
            />
          </div>
        </div>
      </div>
    </>
  )
})
